import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, ImageOff, CheckCircle2, Circle, Search, Filter, Zap } from "lucide-react";
import api from "@/lib/api";

/** /admin/black-1of1 — Phase 1.5 admin management with real image uploads. */
export default function AdminBlackParallels() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("eligible"); // default to the 912 eligible set
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const load = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (filter === "eligible")     params.eligible = true;
    if (filter === "found")        params.status = "found";
    if (filter === "not_surfaced") params.status = "not_surfaced";
    const [r, s] = await Promise.all([
      api.get("/admin/black-1of1/list", { params }).then(r=>r.data).catch(()=>({items:[]})),
      api.get("/black-1of1/stats").then(r=>r.data).catch(()=>null),
    ]);
    setRows(r.items || []);
    setStats(s);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q, filter]);

  const runSeed = async () => {
    if (!window.confirm("Idempotently seed the 912-sticker eligibility population?\n\nThis will NOT overwrite existing FOUND records or admin edits.")) return;
    setSeeding(true); setSeedResult(null);
    try {
      const r = await api.post("/admin/black-1of1/seed-eligibility").then(r=>r.data);
      setSeedResult(r);
      toast.success(`Seeded ${r.total_eligible} eligible · +${r.inserted} new · ${r.tests_reset} test rows isolated`);
      await load();
    } catch(e){
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object") {
        setSeedResult({ error: true, ...d });
        toast.error(d.message || "Seed aborted — see diagnostic");
      } else toast.error(d || "Seed failed");
    } finally { setSeeding(false); }
  };

  const eligibleCount = stats?.total ?? "—";
  const foundCount    = stats?.found ?? "—";
  const percent       = stats == null ? "—" : `${stats.percent}%`;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="admin-black-1of1">
      <div className="container-gm py-6 md:py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1">Admin · Tracker Management</div>
            <h1 className="font-display text-3xl md:text-4xl">Black 1/1 Tracker</h1>
            <p className="text-sm text-neutral-600 mt-1">Manage the eligibility population and evidence for every 1-of-1 parallel.</p>
          </div>
          <button onClick={runSeed} disabled={seeding}
                  data-testid="admin-black-seed"
                  className="inline-flex items-center gap-2 rounded-full bg-black text-white px-5 py-2.5 text-sm font-semibold hover:bg-neutral-800 disabled:opacity-50">
            <Zap size={14} className="text-[#d4af37]" />
            {seeding ? "Seeding…" : "Seed 912 eligibility"}
          </button>
        </div>

        {/* Meta stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <MiniStat label="Eligible population" value={eligibleCount} testid="admin-stat-eligible" />
          <MiniStat label="Publicly surfaced" value={foundCount} accent="green" testid="admin-stat-found" />
          <MiniStat label="% surfaced" value={percent} accent="gold" testid="admin-stat-percent" />
        </div>

        {seedResult && !seedResult.error && (
          <div className="rounded-xl border-2 border-emerald-400 bg-emerald-50 p-4 mb-6 text-sm" data-testid="admin-seed-success">
            <div className="font-semibold text-emerald-900">✓ Seed complete</div>
            <div className="text-emerald-800 mt-1">
              {seedResult.total_eligible} eligible · +{seedResult.inserted} new inserts · {seedResult.updated} re-armed · {seedResult.tests_reset} dummy rows isolated.
            </div>
          </div>
        )}
        {seedResult && seedResult.error && (
          <div className="rounded-xl border-2 border-red-400 bg-red-50 p-4 mb-6 text-sm" data-testid="admin-seed-error">
            <div className="font-semibold text-red-900">╳ Seed aborted</div>
            <div className="text-red-800 mt-1 whitespace-pre-wrap">{seedResult.message}</div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-2 mb-4">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search sticker code, player, country"
                   className="w-full pl-9 pr-3 py-2.5 border-2 border-black/10 rounded-lg text-sm bg-white focus:border-black outline-none" data-testid="admin-black-search" />
          </div>
          <div className="flex items-center gap-2 bg-white border-2 border-black/10 rounded-lg px-3">
            <Filter size={14} className="text-neutral-400" />
            <select value={filter} onChange={e=>setFilter(e.target.value)}
                    className="py-2.5 text-sm bg-transparent outline-none" data-testid="admin-black-filter">
              <option value="all">All catalog</option>
              <option value="eligible">Eligible only</option>
              <option value="found">Found</option>
              <option value="not_surfaced">Not surfaced</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border-2 border-black/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-100">
                <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left p-3">Image</th>
                  <th className="text-left p-3">Code</th>
                  <th className="text-left p-3">Player</th>
                  <th className="text-left p-3">Country</th>
                  <th className="text-center p-3">Eligible</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-t border-black/5 hover:bg-neutral-50" data-testid={`admin-black-row-${r.sticker_number}`}>
                    <td className="p-2">
                      <div className="w-10 h-14 rounded-md bg-neutral-900 overflow-hidden flex items-center justify-center">
                        {r.tracker?.image_url
                          ? <img src={r.tracker.image_url} alt="" className="w-full h-full object-cover" />
                          : <ImageOff size={12} className="text-neutral-600" />}
                      </div>
                    </td>
                    <td className="p-3 font-mono text-xs">{r.sticker_number}</td>
                    <td className="p-3">{r.name}</td>
                    <td className="p-3 font-mono text-xs text-neutral-500">{r.country_code || "—"}</td>
                    <td className="p-3 text-center">
                      {r.tracker?.eligible ? <CheckCircle2 size={16} className="inline text-emerald-600" /> : <Circle size={16} className="inline text-neutral-300" />}
                    </td>
                    <td className="p-3 text-center">
                      {r.tracker?.is_test
                        ? <span className="inline-block text-[9px] px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 font-mono uppercase tracking-widest">TEST</span>
                        : r.tracker?.status === "found"
                          ? <span className="inline-block text-[9px] px-2 py-0.5 rounded-full bg-emerald-500 text-black font-mono uppercase tracking-widest">Found</span>
                          : r.tracker?.eligible
                            ? <span className="inline-block text-[9px] px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-600 font-mono uppercase tracking-widest">Not surfaced</span>
                            : <span className="text-neutral-300">—</span>}
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={()=>setEditing(r)} data-testid={`admin-black-edit-${r.sticker_number}`}
                              className="text-xs font-semibold rounded-full border-2 border-black/20 hover:border-black px-3 py-1 transition">
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-neutral-400">No stickers match your filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editing && <Editor row={editing} onClose={()=>{setEditing(null); load();}} />}
    </div>
  );
}

function MiniStat({label, value, accent, testid}) {
  const c = accent === "green" ? "text-emerald-600"
          : accent === "gold"  ? "text-[#b8860b]"
          : accent === "warn"  ? "text-amber-600"
          : "text-black";
  return (
    <div className="rounded-xl bg-white border-2 border-black/10 p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">{label}</div>
      <div className={`font-display text-2xl ${c}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function Editor({row, onClose}) {
  const t = row.tracker || {};
  const [form, setForm] = useState({
    official_sticker_id: row.id,
    eligible: t.eligible ?? true,
    status: t.status || "not_surfaced",
    first_surfaced_at: t.first_surfaced_at || "",
    owner_display_name: t.owner_display_name || "",
    owner_social_url: t.owner_social_url || "",
    owner_privacy: t.owner_privacy || "private",
    image_media_id: t.image_media_id || null,
    image_url: t.image_url || "",
    source_url: t.source_url || "",
    admin_notes: t.admin_notes || "",
    is_test: !!t.is_test,
  });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post(`/admin/black-1of1/${row.id}/image/upload`, fd,
        { headers: {"Content-Type": "multipart/form-data"} }).then(r=>r.data);
      setForm(x => ({...x, image_media_id: r.media_id, image_url: r.image_url}));
      toast.success("Image uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = async () => {
    if (!form.image_media_id && !form.image_url) return;
    if (!window.confirm("Remove this evidence image?")) return;
    try {
      await api.delete(`/admin/black-1of1/${row.id}/image`);
      setForm(x => ({...x, image_media_id: null, image_url: ""}));
      toast.success("Image removed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Remove failed"); }
  };

  const save = async () => {
    try {
      // Defence-in-depth: normalise blank optional strings to null so we never
      // POST "" for fields that mean "not set". Server also enforces this.
      const BLANK_TO_NULL = ["image_url","source_url","owner_display_name","owner_social_url","image_media_id","admin_notes","first_surfaced_at"];
      const payload = { ...form };
      BLANK_TO_NULL.forEach((k) => {
        if (typeof payload[k] === "string" && !payload[k].trim()) payload[k] = null;
      });
      await api.post("/admin/black-1of1/upsert", payload);
      toast.success("Saved"); onClose();
    } catch(e){ toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e=>e.stopPropagation()}>
        {/* Header banner with sticker context */}
        <div className="bg-[#0b0b0d] text-white p-5 rounded-t-2xl">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#d4af37] font-mono mb-1">{row.country_code || "—"} · {row.sticker_type}</div>
          <div className="font-mono text-sm text-[#d4af37]">{row.sticker_number}</div>
          <h2 className="font-display text-2xl md:text-3xl mt-0.5">{row.name}</h2>
        </div>

        <div className="p-5 grid md:grid-cols-[280px_1fr] gap-5">
          {/* IMAGE UPLOADER */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-mono">Evidence image</div>
            <div className="aspect-[3/4] rounded-xl bg-neutral-900 border-2 border-black/10 overflow-hidden flex items-center justify-center relative" data-testid="admin-black-image-preview">
              {form.image_url ? (
                <img src={form.image_url} alt="Evidence" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-neutral-500">
                  <ImageOff size={36} className="mx-auto mb-2 opacity-40" />
                  <div className="text-[10px] uppercase tracking-widest font-mono">No image</div>
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <div className="text-white text-xs font-mono uppercase tracking-widest animate-pulse">Uploading…</div>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile}
                   className="hidden" data-testid="admin-black-image-input" />
            <div className="flex gap-2 mt-3">
              <button onClick={()=>fileRef.current?.click()} disabled={uploading}
                      data-testid="admin-black-upload-btn"
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-black text-white px-4 py-2 text-xs font-semibold hover:bg-neutral-800 disabled:opacity-50 transition">
                <Upload size={14} /> {form.image_url ? "Replace" : "Upload"}
              </button>
              {form.image_url && (
                <button onClick={removeImage} data-testid="admin-black-remove-btn"
                        className="inline-flex items-center justify-center rounded-full border-2 border-black/20 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <p className="text-[10px] text-neutral-500 mt-2 leading-relaxed">
              JPEG, PNG, or WebP · stored in Emergent Object Storage · served via <span className="font-mono">/api/black-1of1/media</span>.
            </p>
          </div>

          {/* METADATA */}
          <div className="grid gap-3 content-start">
            <SelectField k="eligible" label="Eligible" form={form} setForm={setForm} bool
              options={[{value:"true",label:"Yes — in the 912 population"},{value:"false",label:"No — excluded"}]} />
            <SelectField k="status" label="Discovery status" form={form} setForm={setForm}
              options={[{value:"not_surfaced",label:"Not yet publicly surfaced"},{value:"found",label:"Found (publicly verified)"}]} />
            <TextField k="first_surfaced_at" label="First surfaced (ISO datetime)" form={form} setForm={setForm}
              placeholder="2026-02-15T14:00:00Z" />
            <TextField k="owner_display_name" label="Known owner display name" form={form} setForm={setForm}
              placeholder="e.g. @collectorx" />
            <TextField k="owner_social_url" label="Owner social media URL" form={form} setForm={setForm}
              placeholder="https://instagram.com/… (only shown publicly when privacy is Public)" />
            <SelectField k="owner_privacy" label="Owner privacy" form={form} setForm={setForm}
              options={[{value:"public",label:"Public — show name and social link"},{value:"name_only",label:"Name only — show name, hide social"},{value:"private",label:"Private — hide owner completely"}]} />
            <TextField k="source_url" label="Evidence URL (documents the sighting)" form={form} setForm={setForm}
              placeholder="https://…" />
            <TextField k="admin_notes" label="Admin notes (internal)" form={form} setForm={setForm} textarea />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-black/10 p-4">
          <button onClick={onClose} data-testid="admin-black-cancel"
                  className="text-xs px-4 py-2 rounded-full border-2 border-black/20 hover:bg-neutral-50">Cancel</button>
          <button onClick={save} data-testid="admin-black-save"
                  className="text-xs px-5 py-2 rounded-full bg-black text-white font-semibold hover:bg-neutral-800">Save changes</button>
        </div>
      </div>
    </div>
  );
}

function TextField({k, label, form, setForm, placeholder, textarea}) {
  const Cmp = textarea ? "textarea" : "input";
  return (
    <label className="block text-xs">
      <div className="uppercase tracking-widest text-neutral-500 mb-1 font-mono text-[10px]">{label}</div>
      <Cmp value={form[k] || ""} placeholder={placeholder}
        onChange={e=>setForm(f=>({...f,[k]:e.target.value}))}
        data-testid={`admin-black-field-${k}`}
        rows={textarea ? 3 : undefined}
        className="w-full border-2 border-black/15 rounded-lg px-3 py-2 focus:border-black outline-none text-sm" />
    </label>
  );
}
function SelectField({k, label, form, setForm, options, bool}) {
  return (
    <label className="block text-xs">
      <div className="uppercase tracking-widest text-neutral-500 mb-1 font-mono text-[10px]">{label}</div>
      <select value={bool ? String(!!form[k]) : (form[k] || "")}
        onChange={e=>setForm(f=>({...f, [k]: bool ? (e.target.value==="true") : e.target.value}))}
        data-testid={`admin-black-field-${k}`}
        className="w-full border-2 border-black/15 rounded-lg px-3 py-2 focus:border-black outline-none text-sm">
        {options.map(o=><option key={String(o.value)} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
